import { createUniqueSorKey } from '../work-queues/work-queues-api-mappers';
import {
  type InspectionEvidenceKind,
  type InspectionMediaType,
  type InspectionTemplate,
  type InspectionTemplateItemDraft,
  type InspectionTemplateItemRecord,
  type InspectionTemplateItemRecordData,
  type InspectionTemplateRecord,
  type InspectionTemplateRecordData,
} from './inspection-templates-types';

/**
 * The capture type is stored in `question.evidencePolicy.allowedKind`. Items saved before that
 * field existed carry it as a `media:<type>` tag instead, which is still read and is dropped the
 * next time the item is saved. Items with neither are treated as photo items.
 */
const LEGACY_MEDIA_TAG_PREFIX = 'media:';
const DEFAULT_MEDIA_TYPE: InspectionMediaType = 'photo';

const MEDIA_TYPE_TO_EVIDENCE_KIND: Record<InspectionMediaType, InspectionEvidenceKind> = {
  photo: 'PHOTO',
  video: 'VIDEO',
  photo_video: 'PHOTO_VIDEO',
  document: 'DOCUMENT',
};

/** `subjectKinds` needs at least one entry; inspections on this screen are vehicle inspections */
export const DEFAULT_SUBJECT_KINDS = ['VEHICLE'];

const NAME_MAX_LENGTH = 200;
const CATEGORY_MAX_LENGTH = 100;
const TITLE_MAX_LENGTH = 500;

const isMediaType = (value: unknown): value is InspectionMediaType =>
  typeof value === 'string' && value in MEDIA_TYPE_TO_EVIDENCE_KIND;

function mediaTypeFromEvidenceKind(kind: unknown): InspectionMediaType | null {
  const match = Object.entries(MEDIA_TYPE_TO_EVIDENCE_KIND).find(([, value]) => value === kind);
  return match ? (match[0] as InspectionMediaType) : null;
}

function mediaTypeFromLegacyTag(tags: unknown): InspectionMediaType | null {
  for (const tag of Array.isArray(tags) ? tags : []) {
    if (typeof tag !== 'string' || !tag.startsWith(LEGACY_MEDIA_TAG_PREFIX)) continue;
    const value = tag.slice(LEGACY_MEDIA_TAG_PREFIX.length);
    if (isMediaType(value)) return value;
  }
  return null;
}

export function getMediaType(data: Partial<InspectionTemplateItemRecordData> | undefined): InspectionMediaType {
  return mediaTypeFromEvidenceKind(data?.question?.evidencePolicy?.allowedKind)
    ?? mediaTypeFromLegacyTag(data?.tags)
    ?? DEFAULT_MEDIA_TYPE;
}

/** True when the item still keeps its media type only in the legacy tag */
function needsEvidenceKindMigration(data: Partial<InspectionTemplateItemRecordData> | undefined): boolean {
  return !data?.question?.evidencePolicy?.allowedKind && mediaTypeFromLegacyTag(data?.tags) !== null;
}

/** Tags without the legacy media tag; every other tag is kept */
export function withoutLegacyMediaTag(tags: unknown): string[] {
  return (Array.isArray(tags) ? tags : []).filter(
    (tag): tag is string => typeof tag === 'string' && !tag.startsWith(LEGACY_MEDIA_TAG_PREFIX),
  );
}

export function isRequiredItem(item: Pick<InspectionTemplateItemRecord, 'data'>): boolean {
  return item.data?.question?.required === true;
}

/** Sections only group items, so only questions count as checklist items */
function isChecklistItem(item: Pick<InspectionTemplateItemRecord, 'data'>): boolean {
  return item.data?.itemType !== 'SECTION';
}

/** Title of the shared section, created when SOR has no section yet */
export const DEFAULT_SECTION_TITLE = 'General';

/** `data` for the one section every template's questions are placed under */
export function buildSectionRecordData(
  templateId: string,
  takenItemKeys: Set<string>,
): InspectionTemplateItemRecordData {
  const itemKey = createUniqueSorKey(DEFAULT_SECTION_TITLE, takenItemKeys, 'section');
  takenItemKeys.add(itemKey);
  return { templateId, itemKey, itemType: 'SECTION', title: DEFAULT_SECTION_TITLE, position: 0, tags: [] };
}

export function sortItems<T extends Pick<InspectionTemplateItemRecord, 'data' | 'created_at'>>(
  items: T[],
): T[] {
  return [...items].sort(
    (a, b) => (a.data?.position ?? 0) - (b.data?.position ?? 0)
      || (a.created_at ?? '').localeCompare(b.created_at ?? ''),
  );
}

export function mapInspectionTemplate(
  record: InspectionTemplateRecord,
  items: InspectionTemplateItemRecord[] | null,
): InspectionTemplate {
  const checklist = items?.filter(isChecklistItem);
  return {
    id: record.id,
    revision: record.revision,
    name: record.data?.name || record.display || 'Untitled Template',
    type: record.data?.category ?? '',
    templateKey: record.data?.templateKey ?? record.natural_key ?? '',
    itemCount: checklist ? checklist.length : null,
    requiredItemCount: checklist ? checklist.filter(isRequiredItem).length : null,
    record,
  };
}

/** templateId -> its item records */
export function groupItemsByTemplate(
  items: InspectionTemplateItemRecord[],
): Map<string, InspectionTemplateItemRecord[]> {
  const groups = new Map<string, InspectionTemplateItemRecord[]>();
  for (const item of items) {
    const templateId = item.data?.templateId;
    if (!templateId) continue;
    const group = groups.get(templateId);
    if (group) group.push(item);
    else groups.set(templateId, [item]);
  }
  return groups;
}

export function buildTemplateRecordData(
  name: string,
  existingTemplateKeys: Iterable<string>,
  source?: InspectionTemplateRecordData,
): InspectionTemplateRecordData {
  const trimmedName = name.trim().slice(0, NAME_MAX_LENGTH);
  return {
    // A duplicate keeps everything the source has except its identity
    ...(source ?? {}),
    name: trimmedName,
    templateKey: createUniqueSorKey(trimmedName, existingTemplateKeys, 'inspection_template'),
    version: 1,
    locale: source?.locale ?? 'en',
    tags: source?.tags ?? [],
    subjectKinds: source?.subjectKinds?.length ? source.subjectKinds : DEFAULT_SUBJECT_KINDS,
  };
}

export function normalizeCategory(type: string): string | null {
  const trimmed = type.trim().slice(0, CATEGORY_MAX_LENGTH);
  return trimmed || null;
}

export function itemDraftFromRecord(record: InspectionTemplateItemRecord): InspectionTemplateItemDraft {
  return {
    localId: record.id,
    title: record.data?.title ?? '',
    mediaType: getMediaType(record.data),
    required: isRequiredItem(record),
    record,
  };
}

/**
 * `data` for saving an item draft. An existing item keeps the fields this screen doesn't
 * edit (question options, evidence policy, help text...); a new one gets a unique itemKey.
 */
export function buildItemRecordData(
  draft: InspectionTemplateItemDraft,
  templateId: string,
  position: number,
  takenItemKeys: Set<string>,
  parentItemId: string,
): InspectionTemplateItemRecordData {
  const title = draft.title.trim().slice(0, TITLE_MAX_LENGTH);
  const existing = draft.record?.data;
  const itemKey = existing?.itemKey ?? createUniqueSorKey(title, takenItemKeys, 'item');
  takenItemKeys.add(itemKey);
  return {
    ...(existing ?? {}),
    templateId,
    itemKey,
    itemType: existing?.itemType ?? 'QUESTION',
    parentItemId,
    title,
    position,
    tags: withoutLegacyMediaTag(existing?.tags),
    question: {
      responseType: 'BOOLEAN',
      ...(existing?.question ?? {}),
      required: draft.required,
      evidencePolicy: {
        ...(existing?.question?.evidencePolicy ?? {}),
        allowedKind: MEDIA_TYPE_TO_EVIDENCE_KIND[draft.mediaType],
      },
    },
  };
}

/** True when saving the draft would change the stored item */
export function hasItemChanged(
  draft: InspectionTemplateItemDraft,
  position: number,
  parentItemId?: string,
): boolean {
  const record = draft.record;
  if (!record) return true;
  return record.data?.title !== draft.title.trim()
    || (record.data?.position ?? -1) !== position
    // Moves an item onto the shared section
    || (parentItemId !== undefined && record.data?.parentItemId !== parentItemId)
    || getMediaType(record.data) !== draft.mediaType
    || isRequiredItem(record) !== draft.required
    // Moves the media type off the legacy tag even when nothing else changed
    || needsEvidenceKindMigration(record.data);
}
